import { useEffect, useState } from 'react';
import { cn } from './ui-x';

type Nullable = string | null | undefined;

const initials = (name?: string | null) =>
  (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '—';

/*
 * The placeholder the API hands back when nobody has uploaded a photo, for
 * user photos and company logos alike. It is a real URL, so `src ? <img>`
 * renders it happily -- which is how a cartoon ended up standing in for people
 * who simply have no picture. Matched on the cloudinary public id rather than
 * the whole URL so a size or format transform in the path does not slip past
 * it. clientApp treats the same image as "no photo".
 */
const DEFAULT_PHOTO_ID = 'fyd319ojzgxvzqlhgv9x';

/* A type predicate, so the <img> below still narrows src to a string the
   way the old `src ? ` check did. */
const hasPhoto = (src?: Nullable): src is string =>
  !!src && !src.includes(DEFAULT_PHOTO_ID);

/** The inverse, for avatars outside this component (the navbar's). */
export const isDefaultPhoto = (url: Nullable) => !hasPhoto(url);

/**
 * A photo or logo with an initials fallback. The fallback covers a missing
 * picture, the default placeholder, and an image that fails to load (an
 * expired or deleted upload), which would otherwise paint a broken-image glyph.
 *
 * `textClassName` sizes the initials; the default suits the small table and
 * list avatars, and large avatars pass a bigger size.
 */
const EntityAvatar = ({
  src,
  name,
  className = 'h-9 w-9',
  textClassName = 'text-[11px]',
}: {
  src?: Nullable;
  name?: Nullable;
  className?: string;
  textClassName?: string;
}) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  return !failed && hasPhoto(src) ? (
    <img
      src={src}
      alt=''
      aria-hidden='true'
      onError={() => setFailed(true)}
      className={cn('shrink-0 rounded-full border object-cover', className)}
    />
  ) : (
    <span
      aria-hidden='true'
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-brand-navy-100 font-bold text-brand-navy',
        textClassName,
        className
      )}
    >
      {initials(name)}
    </span>
  );
};

export default EntityAvatar;
